import {DatePicker, Form, InputNumber, Modal, Radio, Select} from 'antd';
import React, {useEffect} from 'react';

import {useAdminCreateProductOrderMutation} from '../../../../gen/api/apis';
import {AdminOrderPaymentType, AdminOrderStatusType, AdminProductDetailMarketplace} from '../../../../gen/api/models';
import {useNotification} from '../../../../NotificationContext';
import {orderPaymentTypes} from '../../../helpers/labels';

export interface ProductOrderCreateProps {
    productId: string;
    marketplaces: Array<AdminProductDetailMarketplace>;
    onOrderCreated: () => void;
    onClosed: () => void;
}

interface Values {
    marketplaceID?: number;
    createdAt?: string;
    orderStatus?: AdminOrderStatusType;
    salePrice: number;
    paymentType: AdminOrderPaymentType;
}

export const ProductOrderCreate = (props: ProductOrderCreateProps) => {
    const notify = useNotification();
    const [form] = Form.useForm();
    const [isMarketplace, setIsMarketplace] = React.useState(true);

    useEffect(() => {
        if (!isMarketplace) {
            form.setFieldsValue({marketplaceID: undefined});
        }
    }, [isMarketplace]);

    const createOrder = useAdminCreateProductOrderMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon('success', 'Order created');
            props.onOrderCreated();
        },
        onError: (error) => {
            console.error(error);
            notify.openNotificationWithIcon('error', 'Error create order');
        },
    });

    const create = (values: Values) => {
        createOrder.mutate({
            id: props.productId,
            params: {
                marketplaceID: values.marketplaceID,
                createdAt: values.createdAt,
                orderStatus: values.orderStatus,
                salePrice: values.salePrice,
                paymentType: values.paymentType,
            },
        });
    };
    return (
        <Modal
            open
            title="Create Order"
            onCancel={props.onClosed}
            confirmLoading={createOrder.isLoading}
            okText="Create"
            okButtonProps={{autoFocus: true, htmlType: 'submit'}}
            cancelText="Cancel"
            maskClosable={false}
            centered
            destroyOnClose
            modalRender={(dom) => (
                <Form
                    layout="horizontal"
                    labelCol={{span: 6}}
                    wrapperCol={{span: 18}}
                    form={form}
                    name="newOrder"
                    clearOnDestroy
                    onFinish={(values) => create(values)}
                >
                    {dom}
                </Form>
            )}
        >
            <Radio.Group
                defaultValue={true}
                onChange={(e) => setIsMarketplace(e.target.value)}
                style={{display: 'flex', justifyContent: 'center', marginBottom: 20, marginTop: 20}}
            >
                <Radio.Button
                    value={true}
                    style={{width: '100%', textAlign: 'center'}}
                >
                    Marketplace Order
                </Radio.Button>
                <Radio.Button
                    value={false}
                    style={{width: '100%', textAlign: 'center'}}
                >
                    Self Order
                </Radio.Button>
            </Radio.Group>
            <Form.Item
                name="salePrice"
                label="Sale Price"
                rules={[{required: true, message: 'Please specify sale price!'}]}
            >
                <InputNumber />
            </Form.Item>
            <Form.Item
                hidden={!isMarketplace}
                name="marketplaceID"
                label="Marketplace"
                rules={[{required: isMarketplace, message: 'Please select marketplace!'}]}
            >
                <Select
                    showSearch
                    placeholder="Select a marketplace"
                >
                    {props.marketplaces.map((marketplace) => (
                        <Select.Option
                            key={marketplace.id}
                            value={marketplace.id}
                        >
                            {marketplace.name} - ${marketplace.price}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item
                name="paymentType"
                label="Payment type"
                rules={[{required: true, message: 'Please select payment type!'}]}
            >
                <Select
                    placeholder="Select payment type"
                    allowClear
                    options={orderPaymentTypes.map((type) => ({
                        label: type.label,
                        value: type.key,
                    }))}
                />
            </Form.Item>
            <Form.Item
                name="orderStatus"
                label="Specific status"
            >
                <Select
                    placeholder="Default 'Created'"
                    allowClear
                    options={[
                        {label: 'Paid', value: AdminOrderStatusType.OrderStatusPaid},
                        {label: 'Canceled', value: AdminOrderStatusType.OrderStatusCanceled},
                        {label: 'Returned', value: AdminOrderStatusType.OrderStatusReturned},
                    ]}
                />
            </Form.Item>

            <Form.Item
                name="createdAt"
                label="Specific date"
            >
                <DatePicker placeholder={'Right now'} />
            </Form.Item>
        </Modal>
    );
};
